import { useMemo } from 'react';

const ROW_COUNTS = [3, 9, 9, 9, 9, 3];
const ROW_HALF_SIDE_OFFSETS = [3, 0, 0, 0, 0, 3];
const ROW_STARTS_UP = [true, true, false, true, false, false];
const ADJACENT_HEXAGONS = [
  [0, 1, 2, 6, 7, 8],
  [3, 4, 5, 12, 13, 14],
  [5, 6, 7, 14, 15, 16],
  [7, 8, 9, 16, 17, 18],
  [9, 10, 11, 18, 19, 20],
  [13, 14, 15, 22, 23, 24],
  [15, 16, 17, 24, 25, 26],
  [17, 18, 19, 26, 27, 28],
  [21, 22, 23, 30, 31, 32],
  [23, 24, 25, 32, 33, 34],
  [25, 26, 27, 34, 35, 36],
  [27, 28, 29, 36, 37, 38],
  [33, 34, 35, 39, 40, 41],
];

const pointKey = (point) => `${point.x.toFixed(6)},${point.y.toFixed(6)}`;

const edgeKey = (start, end) => {
  const keys = [pointKey(start), pointKey(end)].sort();
  return `${keys[0]}|${keys[1]}`;
};

const parsePointKey = (key) => {
  const [x, y] = key.split(',').map(Number);
  return { x, y };
};

const polygonPoints = (points) =>
  points.map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' ');

const buildGeometry = () => {
  const triangleSide = 108;
  const triangleWidth = triangleSide * 1.04;
  const halfTriangleWidth = triangleWidth / 2;
  const triangleHeight = (triangleSide * Math.sqrt(3)) / 2;
  const margin = 36;
  const cells = [];
  const edgeCounts = new Map();
  let index = 0;

  ROW_COUNTS.forEach((count, row) => {
    const y = margin + row * triangleHeight;
    const xOffset = margin + ROW_HALF_SIDE_OFFSETS[row] * halfTriangleWidth;

    for (let col = 0; col < count; col += 1) {
      const x = xOffset + col * halfTriangleWidth;
      const pointsUp = (col % 2 === 0) === ROW_STARTS_UP[row];
      const points = pointsUp
        ? [
            { x, y: y + triangleHeight },
            { x: x + triangleWidth, y: y + triangleHeight },
            { x: x + halfTriangleWidth, y },
          ]
        : [
            { x, y },
            { x: x + triangleWidth, y },
            { x: x + halfTriangleWidth, y: y + triangleHeight },
          ];

      const centroid = {
        x: points.reduce((sum, point) => sum + point.x, 0) / 3,
        y: points.reduce((sum, point) => sum + point.y, 0) / 3,
      };
      const edges = [
        edgeKey(points[0], points[1]),
        edgeKey(points[1], points[2]),
        edgeKey(points[2], points[0]),
      ];

      edges.forEach((key) => edgeCounts.set(key, (edgeCounts.get(key) || 0) + 1));
      cells.push({ index, points, centroid, pointsUp });
      index += 1;
    }
  });

  const allPoints = cells.flatMap((cell) => cell.points);
  const minX = Math.min(...allPoints.map((point) => point.x)) - margin;
  const maxX = Math.max(...allPoints.map((point) => point.x)) + margin;
  const minY = Math.min(...allPoints.map((point) => point.y)) - margin;
  const maxY = Math.max(...allPoints.map((point) => point.y)) + margin;
  const width = maxX - minX;
  const height = maxY - minY;

  return {
    cells,
    edgeCounts,
    width,
    height,
    hexRadius: triangleWidth * 0.18,
  };
};

const boundaryPath = (edgeCounts) => {
  const adjacency = new Map();

  edgeCounts.forEach((count, key) => {
    if (count !== 1) {
      return;
    }
    const [start, end] = key.split('|');
    adjacency.set(start, [...(adjacency.get(start) || []), end]);
    adjacency.set(end, [...(adjacency.get(end) || []), start]);
  });

  if (!adjacency.size) {
    return '';
  }

  const startKey = [...adjacency.keys()].sort((left, right) => {
    const leftPoint = parsePointKey(left);
    const rightPoint = parsePointKey(right);
    return leftPoint.y - rightPoint.y || leftPoint.x - rightPoint.x;
  })[0];

  const orderedKeys = [startKey];
  let previousKey = null;
  let currentKey = startKey;

  while (true) {
    const neighbors = adjacency.get(currentKey) || [];
    let nextKey = neighbors[0];
    if (previousKey && nextKey === previousKey && neighbors.length > 1) {
      nextKey = neighbors[1];
    }
    if (!nextKey || nextKey === startKey) {
      break;
    }
    orderedKeys.push(nextKey);
    previousKey = currentKey;
    currentKey = nextKey;
  }

  const points = orderedKeys.map(parsePointKey);
  return [
    `M ${points[0].x.toFixed(2)},${points[0].y.toFixed(2)}`,
    ...points.slice(1).map((point) => `L ${point.x.toFixed(2)},${point.y.toFixed(2)}`),
    'Z',
  ].join(' ');
};

const hexagonPoints = (center, radius) =>
  Array.from({ length: 6 }, (_, step) => {
    const radians = (Math.PI / 180) * 60 * step;
    return {
      x: center.x + radius * Math.cos(radians),
      y: center.y + radius * Math.sin(radians),
    };
  });

export default function SnowflakeBoard({
  values = [],
  givens = [],
  assignedCells = [],
  eliminatedCells = [],
  overlapCells = [],
  candidateDisplays = [],
}) {
  const geometry = useMemo(buildGeometry, []);
  const boundary = useMemo(() => boundaryPath(geometry.edgeCounts), [geometry.edgeCounts]);
  const givenSet = useMemo(() => new Set(givens), [givens]);
  const assignedSet = useMemo(() => new Set(assignedCells), [assignedCells]);
  const eliminatedSet = useMemo(() => new Set(eliminatedCells), [eliminatedCells]);
  const overlapSet = useMemo(() => new Set(overlapCells), [overlapCells]);
  const candidateDisplayMap = useMemo(
    () =>
      new Map(
        candidateDisplays.map((entry) => [
          entry.cell,
          {
            values: Array.isArray(entry.values) ? entry.values : [],
            circledValues: new Set(
              Array.isArray(entry.circledValues) ? entry.circledValues : []
            ),
          },
        ])
      ),
    [candidateDisplays]
  );

  const renderCandidateValues = (cell, candidateDisplay, fill, circleStroke, keyPrefix) => {
    const candidateValues = candidateDisplay.values;
    if (!candidateValues.length || values[cell.index]) {
      return null;
    }

    const fontSize = candidateValues.length > 4 ? 12 : 14;
    const spacing = candidateValues.length > 4 ? 10 : 12;
    const y = cell.centroid.y + (cell.pointsUp ? 7 : 11);

    return candidateValues.map((candidateValue, index) => {
      const x = cell.centroid.x + (index - (candidateValues.length - 1) / 2) * spacing;
      const isCircled = candidateDisplay.circledValues.has(candidateValue);

      return (
        <g key={`${keyPrefix}-${cell.index}-${candidateValue}`}>
          {isCircled ? (
            <circle
              cx={x.toFixed(2)}
              cy={(y - 4).toFixed(2)}
              r="8.2"
              fill="none"
              stroke={circleStroke}
              strokeWidth="2"
            />
          ) : null}
          <text
            x={x.toFixed(2)}
            y={y.toFixed(2)}
            textAnchor="middle"
            fontFamily="Helvetica, Arial, sans-serif"
            fontSize={fontSize}
            fontWeight="700"
            fill={fill}
          >
            {candidateValue}
          </text>
        </g>
      );
    });
  };

  return (
    <svg
      viewBox={`0 0 ${geometry.width.toFixed(2)} ${geometry.height.toFixed(2)}`}
      role="img"
      aria-label="Snowflake Sudoku puzzle"
      className="h-auto w-full"
    >
      <rect width={geometry.width} height={geometry.height} fill="#ffffff" />

      {geometry.cells.map((cell) => {
        const isAssigned = assignedSet.has(cell.index);
        const isEliminated = eliminatedSet.has(cell.index);
        const isOverlap = overlapSet.has(cell.index);
        const fill = isAssigned
          ? '#d9fbfd'
          : isEliminated
          ? '#fff3bf'
          : isOverlap
          ? '#dbeafe'
          : '#ffffff';

        return (
          <polygon
            key={cell.index}
            points={polygonPoints(cell.points)}
            fill={fill}
            stroke="#111827"
            strokeWidth="2.4"
            strokeLinejoin="round"
          />
        );
      })}

      {ADJACENT_HEXAGONS.map((hexagon, index) => {
        const center = {
          x:
            hexagon.reduce((sum, cellIndex) => sum + geometry.cells[cellIndex].centroid.x, 0) /
            hexagon.length,
          y:
            hexagon.reduce((sum, cellIndex) => sum + geometry.cells[cellIndex].centroid.y, 0) /
            hexagon.length,
        };

        return (
          <g key={index}>
            <polygon
              points={polygonPoints(hexagonPoints(center, geometry.hexRadius))}
              fill="#111827"
              stroke="#111827"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <text
              x={center.x.toFixed(2)}
              y={(center.y + 5).toFixed(2)}
              textAnchor="middle"
              fontFamily="Helvetica, Arial, sans-serif"
              fontSize="18"
              fontWeight="700"
              fill="#ffffff"
            >
              {index + 1}
            </text>
          </g>
        );
      })}

      {boundary ? (
        <path
          d={boundary}
          fill="none"
          stroke="#111827"
          strokeWidth="7"
          strokeLinejoin="miter"
          strokeLinecap="butt"
        />
      ) : null}

      {geometry.cells.map((cell) => {
        if (candidateDisplayMap.has(cell.index)) {
          const isOverlap = overlapSet.has(cell.index);
          const isEliminated = eliminatedSet.has(cell.index);

          return renderCandidateValues(
            cell,
            candidateDisplayMap.get(cell.index),
            isOverlap ? '#075985' : isEliminated ? '#92400e' : '#475569',
            isOverlap ? '#0284c7' : '#92400e',
            'candidate'
          );
        }

        return null;
      })}

      {geometry.cells.map((cell) => {
        const value = values[cell.index];
        if (!value) {
          return null;
        }

        const isGiven = givenSet.has(cell.index);
        const yBias = cell.pointsUp ? 1.4 : 2.8;

        return (
          <text
            key={`value-${cell.index}`}
            x={cell.centroid.x.toFixed(2)}
            y={(cell.centroid.y + yBias + 14).toFixed(2)}
            textAnchor="middle"
            fontFamily="Helvetica, Arial, sans-serif"
            fontSize="39"
            fontWeight={isGiven ? '700' : '600'}
            fill={isGiven ? '#111827' : '#d9007d'}
          >
            {value}
          </text>
        );
      })}
    </svg>
  );
}
